import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { telephonyApi } from '../telephonyApi';
import type { CallHistoryFilters, CallHistoryResult } from '../types';

export function buildCallHistoryRequest(filters: CallHistoryFilters = {}) {
  const rawPage = Number(filters.page || 1);
  const rawPageSize = Number(filters.pageSize || 20);
  const page = Number.isFinite(rawPage) ? Math.max(1, Math.floor(rawPage)) : 1;
  const pageSize = Number.isFinite(rawPageSize)
    ? Math.min(100, Math.max(5, Math.floor(rawPageSize)))
    : 20;
  const fetchLimit = Math.min(500, Math.max(pageSize, page * pageSize + 1));
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.direction) params.set('direction', filters.direction);
  if (filters.status) params.set('status', filters.status);
  if (filters.routeGroupId) params.set('routeGroupId', filters.routeGroupId);
  if (filters.operatorUserId) params.set('operatorUserId', filters.operatorUserId);
  params.set('limit', String(fetchLimit));
  return { params, page, pageSize, fetchLimit };
}

export function useCallHistory(filters: CallHistoryFilters = {}) {
  const request = buildCallHistoryRequest(filters);
  const { page, pageSize } = request;

  return useQuery<CallHistoryResult>({
    queryKey: [
      'telephony-calls',
      filters.search || '',
      filters.direction || '',
      filters.status || '',
      filters.routeGroupId || '',
      filters.operatorUserId || '',
      page,
      pageSize,
    ],
    queryFn: async ({ signal }) => {
      const rows = await telephonyApi.calls(request.params, signal);
      const start = (page - 1) * pageSize;
      const pageRows = rows.slice(start, start + pageSize);
      return {
        rows: pageRows,
        totalLoaded: rows.length,
        page,
        pageSize,
        hasPrevious: page > 1,
        hasNext: rows.length > start + pageSize,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 5_000,
    refetchInterval: 10_000,
  });
}
